// On-course scoring, in a phone browser, with nothing installed.
//
// This is the page that lets a real outing run before the iOS app clears review. A
// scorekeeper types a six-character code off a cart sign, takes a seat on their team's
// card, and starts entering numbers. No App Store, no signup, no password — an anonymous
// Supabase session is the whole identity, and it is enough because `claim_entrant` binds
// that session to one seat and RLS scopes every write to that group's card.
//
// Three things drove every decision in here:
//
//   BAD SIGNAL IS NORMAL. A course drops coverage in every tree line. So no write is ever
//   awaited before the number appears on screen, and no failure is ever allowed to discard
//   a typed score. A failed write goes back on the retry table and keeps going until it
//   lands. The worst outcome this page permits is a stale server, never a lost hole.
//
//   A SHOTGUN IS THE DEFAULT, not the exception. A group off hole 5 plays 5…18,1…4, so
//   "which hole is next" and "how far round are they" are different questions from "what
//   number is on the sign". Both are answered by the shared playOrder helpers, so the
//   phone app, the console and this page cannot disagree.
//
//   A SCRAMBLE IS ONE NUMBER. Four people, one ball, one score — written against every
//   member of the team. That is not duplication (see the note in 0001_schema.sql): they
//   all made it, and keeping real people on the card is what lets a closest-to-the-pin
//   belong to a person instead of a team.
//
// PLACEHOLDER FOR PATRICK: four phones in a group can each hold a seat on the same card,
// but they only see each other's scores on a manual refresh — there is no realtime
// subscription here. The tables are already in the `supabase_realtime` publication, so it
// is a small addition; the reason it is not in yet is that a live feed has to be merged
// against the optimistic state without ever reverting a number somebody just typed, and
// that is worth designing rather than bolting on. Is one scorekeeper per group the
// expected way this runs, or do you want all four phones live?
//
// PLACEHOLDER FOR PATRICK: if a scorekeeper clears their browser data or switches phones
// mid-round, their seat still shows as claimed by the old anonymous identity and
// `claim_entrant` refuses it. Only the host can release it (`withdraw_entrant`, staff
// only). Should a player be able to take back a seat they can no longer prove is theirs,
// or is "go and see the scorer's table" the right answer? The safe one is the current
// behaviour, which is why it is what is here.

import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { errText, supabase } from '../lib/supabase';
import { useAnonSession } from '../lib/useAnonSession';
import HoleCard from '../components/HoleCard';
import type { ContestEntrant, HoleCardContest, Stepper } from '../components/HoleCard';
import type { WolfPickBlock, WolfPickValue } from '../components/WolfPick';
// Split out, for the same reason the console is: these two pull in all nineteen game
// engines, which is 23kB gzipped that a scorekeeper does not need before they can put a
// number on the first hole. This page is fetched over the worst connection in the product.
// The type import beside them costs nothing — types are erased.
const GameSetup = lazy(() => import('../components/GameSetup'));
const GroupMoney = lazy(() => import('../components/GroupMoney'));
import type { SavedGame } from '../components/GroupMoney';
import {
  courseFromPlatform,
  hasRealStrokeIndexes,
  roundFromPlatform,
  type PlatformHoleInput,
  type PlatformScore,
} from '@shared/sync/toDomain';
import type { WolfConfig } from '@shared/engine/types';
import {
  formatJoinCode,
  isValidJoinCode,
  joinCodeProblem,
  normalizeJoinCode,
} from '@shared/sync/codes';
import {
  holesPlayed,
  nextUnplayedHole,
  normalizeStartHole,
  playOrder,
  playPosition,
  stepHole,
} from '@shared/engine/playOrder';
import { CONTEST_LABELS } from '@shared/engine/contests';
import { emptyHole } from '@shared/types';
import type { ContestKind, HoleData, Round } from '@shared/types';
import { LoadingScore } from '../components/Loading';

// ── What join_tournament hands back ───────────────────────────────────────────

type JoinTournament = {
  id: string;
  name: string;
  starts_on: string | null;
  format: 'scramble' | 'individual';
  status: 'draft' | 'open' | 'live' | 'final' | 'cancelled';
  scoring: 'gross' | 'net';
  hole_set: 'full' | 'front' | 'back';
  course_name: string;
  hole_pars: number[];
  hole_indexes: number[] | null;
  hole_number_offset: number;
  team_size: number;
};

type JoinEntrant = {
  id: string;
  seat: number;
  display_name: string | null;
  /** Somebody's phone already holds this seat. Ours included — the RPC cannot tell us which. */
  claimed: boolean;
};

type JoinTeam = {
  id: string;
  name: string;
  start_hole: number | null;
  slot_label: string | null;
  entrants: JoinEntrant[];
};

type JoinPayload = {
  tournament: JoinTournament;
  teams: JoinTeam[];
  contests: Array<{ hole_idx: number; kind: ContestKind; label: string | null }>;
};

/** A contest row read from the table, which — unlike the join payload — carries its id. */
type ContestRow = { id: string; hole_idx: number; kind: ContestKind; label: string | null };

type ScoreRow = { entrant_id: string; hole_idx: number; gross: number };
type ClaimRow = { contest_id: string; entrant_id: string; measure: string | null };

/**
 * A row of `round_hole_inputs`: everything a hole carries that is not a score.
 *
 * One jsonb blob per hole for the whole group, holding the app's own `HoleData` minus
 * `scores` — the wolf's pick today, a banker's wagers and junk flags when those inputs
 * arrive. Kept out of `hole_scores` on purpose: scores are the high-traffic write, one per
 * player per hole over a bad connection, and they should not contend with a row the rest of
 * the group also touches.
 */
type HoleInputData = PlatformHoleInput['data'];
type HoleInputRow = { hole_idx: number; data: HoleInputData | null };

// ── The retry table ───────────────────────────────────────────────────────────
//
// Deliberately not a queue. It is a map keyed by CELL, so tapping "+" five times leaves one
// pending write holding the latest number rather than five writes racing each other to the
// server in an order nobody controls. Last write wins, which is also what the schema says.

type Op = {
  /** The cell this write owns. A newer write to the same cell replaces it outright. */
  key: string;
  /** Which hole to mark unsaved. */
  holeIdx: number;
  /** The `entrantId|holeIdx` cells this write covers, so a refresh knows not to revert them. */
  cells: string[];
  run: () => Promise<void>;
  attempts: number;
  /** Epoch ms; not retried before this. */
  nextAt: number;
  inFlight: boolean;
};

/** 1s, 2s, 4s… capped at 15s, with jitter so four phones in a cart do not retry in lockstep. */
function backoff(attempts: number): number {
  const base = Math.min(1000 * 2 ** Math.max(0, attempts - 1), 15000);
  return base + Math.random() * 400;
}

// ── Small helpers ─────────────────────────────────────────────────────────────

const cellKey = (entrantId: string, holeIdx: number) => `${entrantId}|${holeIdx}`;
/** The retry-table key for a hole's non-score inputs. One per hole, so the newest blob wins. */
const holeInputKey = (holeIdx: number) => `holeinput:${holeIdx}`;
const seatStorageKey = (code: string) => `4sum.score.${code}`;

type SavedSeat = { teamId: string; entrantId: string };

function readSavedSeat(code: string): SavedSeat | null {
  try {
    const raw = window.localStorage.getItem(seatStorageKey(code));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SavedSeat>;
    if (typeof parsed?.teamId === 'string' && typeof parsed?.entrantId === 'string') {
      return { teamId: parsed.teamId, entrantId: parsed.entrantId };
    }
  } catch {
    /* A browser with storage disabled just means picking the seat again. Not fatal. */
  }
  return null;
}

function writeSavedSeat(code: string, seat: SavedSeat | null): void {
  try {
    if (seat) window.localStorage.setItem(seatStorageKey(code), JSON.stringify(seat));
    else window.localStorage.removeItem(seatStorageKey(code));
  } catch {
    /* ditto */
  }
}

/** A seat sold but never filled in still needs something to call it. */
const entrantName = (e: JoinEntrant): string =>
  e.display_name?.trim() || `Open spot ${e.seat + 1}`;

const contestText = (kind: ContestKind, label: string | null): string =>
  label?.trim() || CONTEST_LABELS[kind];

// ── The page ──────────────────────────────────────────────────────────────────

export default function ScorePage() {
  const params = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const session = useAnonSession();

  const routeCode = normalizeJoinCode(params.code ?? '');

  const [typed, setTyped] = useState('');
  const [code, setCode] = useState(isValidJoinCode(routeCode) ? routeCode : '');

  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [payload, setPayload] = useState<JoinPayload | null>(null);

  const [pickTeamId, setPickTeamId] = useState<string | null>(null);
  const [claimError, setClaimError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);

  const [teamId, setTeamId] = useState<string | null>(null);
  const [entrantId, setEntrantId] = useState<string | null>(null);
  const [roundId, setRoundId] = useState<string | null>(null);

  const [scores, setScores] = useState<Record<string, number>>({});
  /**
   * `round_hole_inputs`, keyed by hole index. The Wolf pick lives in here.
   *
   * The ref is the truth and the state is its render mirror — the same arrangement the retry
   * table uses, and for the same reason: a write has to merge against the current blob
   * synchronously, before React has processed anything.
   */
  const holeInputsRef = useRef<Record<number, HoleInputData>>({});
  const [holeInputs, setHoleInputs] = useState<Record<number, HoleInputData>>({});
  const [claims, setClaims] = useState<Record<string, { entrantId: string; measure: string }>>({});
  const [contestRows, setContestRows] = useState<ContestRow[]>([]);
  const [scoresLoaded, setScoresLoaded] = useState(false);

  // The group's own side games, and the handicaps a net one would need. Both belong to the
  // side-game section only — nothing about stroke play reads either.
  const [games, setGames] = useState<SavedGame[]>([]);
  const [handicaps, setHandicaps] = useState<Record<string, number>>({});

  const [holeIdx, setHoleIdx] = useState(0);
  const [jumpedToLive, setJumpedToLive] = useState(false);

  const [online, setOnline] = useState(() => navigator.onLine);

  // Pending writes. The ref is the truth; the state mirror exists only so React re-renders
  // the "not saved yet" markers.
  const opsRef = useRef(new Map<string, Op>());
  const [pendingHoles, setPendingHoles] = useState<Record<string, number>>({});
  const flushingRef = useRef(false);

  const syncPendingView = useCallback(() => {
    const view: Record<string, number> = {};
    opsRef.current.forEach((op, key) => {
      view[key] = op.holeIdx;
    });
    setPendingHoles(view);
  }, []);

  const flush = useCallback(async () => {
    if (flushingRef.current) return;
    const now = Date.now();
    const due = [...opsRef.current.values()].filter((o) => !o.inFlight && o.nextAt <= now);
    if (due.length === 0) return;

    flushingRef.current = true;
    for (const op of due) op.inFlight = true;

    await Promise.allSettled(
      due.map(async (op) => {
        try {
          await op.run();
          // Only retire it if nothing newer took the cell while we were away. If something
          // did, that newer op is now the map entry and must still run.
          if (opsRef.current.get(op.key) === op) opsRef.current.delete(op.key);
        } catch {
          op.inFlight = false;
          op.attempts += 1;
          op.nextAt = Date.now() + backoff(op.attempts);
        }
      })
    );

    flushingRef.current = false;
    syncPendingView();
  }, [syncPendingView]);

  const enqueue = useCallback(
    (key: string, holeIdx_: number, cells: string[], run: () => Promise<void>) => {
      opsRef.current.set(key, {
        key,
        holeIdx: holeIdx_,
        cells,
        run,
        attempts: 0,
        nextAt: Date.now(),
        inFlight: false,
      });
      syncPendingView();
      void flush();
    },
    [flush, syncPendingView]
  );

  // The heartbeat. Cheap, and it is what turns "the write failed" into "the write has not
  // landed yet" — the difference between losing a hole and waiting for one.
  useEffect(() => {
    const timer = window.setInterval(() => void flush(), 1500);

    const wakeUp = () => {
      setOnline(navigator.onLine);
      // Coming back from a dead spot should not sit out a fifteen-second backoff.
      opsRef.current.forEach((op) => {
        if (!op.inFlight) op.nextAt = 0;
      });
      void flush();
    };

    const wentAway = () => setOnline(false);

    window.addEventListener('online', wakeUp);
    window.addEventListener('offline', wentAway);
    document.addEventListener('visibilitychange', wakeUp);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', wakeUp);
      window.removeEventListener('offline', wentAway);
      document.removeEventListener('visibilitychange', wakeUp);
    };
  }, [flush]);

  const pendingCount = useMemo(
    () => new Set(Object.values(pendingHoles)).size,
    [pendingHoles]
  );

  // Closing the tab with holes still in the air is the one way a score can actually be
  // lost, so it is the one thing worth interrupting for.
  useEffect(() => {
    if (pendingCount === 0) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [pendingCount]);

  // ── Joining ─────────────────────────────────────────────────────────────────

  const runJoin = useCallback(async (joinCode: string) => {
    setJoining(true);
    setJoinError(null);
    try {
      const { data, error } = await supabase.rpc('join_tournament', { p_join_code: joinCode });
      if (error) throw error;
      setPayload(data as JoinPayload);
    } catch (err) {
      setJoinError(errText(err));
      setPayload(null);
    } finally {
      setJoining(false);
    }
  }, []);

  useEffect(() => {
    if (!code || !session.ready) return;
    void runJoin(code);
  }, [code, session.ready, runJoin]);

  // A code in the URL wins over whatever is in state — somebody re-scanning a different
  // cart sign should land on that event, not the one they were last on.
  useEffect(() => {
    if (isValidJoinCode(routeCode) && routeCode !== code) {
      setCode(routeCode);
      setTeamId(null);
      setEntrantId(null);
      setRoundId(null);
    }
  }, [routeCode, code]);

  const team = useMemo(
    () => payload?.teams.find((t) => t.id === teamId) ?? null,
    [payload, teamId]
  );

  const entrants = team?.entrants ?? [];
  const entrantIds = useMemo(() => entrants.map((e) => e.id), [entrants]);

  /**
   * The people actually playing, as opposed to the seats that exist.
   *
   * A sponsor buys a foursome months out and three of them turn up. Those empty seats are
   * real rows with real ids — and if they counted towards "is this hole finished", a group
   * of three would read "thru 0" all afternoon, because the fourth slot never gets a
   * number. So completion is judged against who is on the card, not how many were sold.
   *
   * Scramble still WRITES to every seat including the empty ones: the schema says the
   * team's score belongs to every member, and an empty seat carrying it costs nothing.
   */
  const cardEntrants = useMemo(() => {
    const playing = entrants.filter((e) => (e.display_name?.trim() ?? '') !== '' || e.claimed);
    return playing.length > 0 ? playing : entrants;
  }, [entrants]);

  const cardEntrantIds = useMemo(() => cardEntrants.map((e) => e.id), [cardEntrants]);

  const tournament = payload?.tournament ?? null;
  const holeCount = tournament?.hole_pars?.length ?? 0;
  const scramble = tournament?.format === 'scramble';
  // A start hole past the end of the played set (a nine-hole event carrying an 18-hole
  // assignment, say) means hole one, not a crash on an undefined par.
  const startHole = normalizeStartHole(team?.start_hole ?? undefined, holeCount);

  // ── Opening the card ────────────────────────────────────────────────────────

  const loadRoundData = useCallback(
    async (rid: string, tournamentId: string) => {
      const [scoreRes, contestRes, claimRes, inputRes] = await Promise.all([
        supabase.from('hole_scores').select('entrant_id,hole_idx,gross').eq('round_id', rid),
        supabase.from('contests').select('id,hole_idx,kind,label').eq('tournament_id', tournamentId),
        supabase.from('contest_claims').select('contest_id,entrant_id,measure').eq('round_id', rid),
        supabase.from('round_hole_inputs').select('*').eq('round_id', rid),
      ]);

      if (!scoreRes.error) {
        const rows = (scoreRes.data ?? []) as ScoreRow[];
        // Merge rather than replace: anything still waiting to sync is newer than whatever
        // the server just told us, and must not be reverted under the scorekeeper. A cell
        // with a pending DELETE stays absent for the same reason.
        setScores((prev) => {
          const pendingCells = new Set<string>();
          opsRef.current.forEach((op) => op.cells.forEach((c) => pendingCells.add(c)));

          const next: Record<string, number> = {};
          for (const row of rows) {
            const key = cellKey(row.entrant_id, row.hole_idx);
            if (!pendingCells.has(key)) next[key] = row.gross;
          }
          for (const key of pendingCells) {
            const value = prev[key];
            if (value != null) next[key] = value;
          }
          return next;
        });
        setScoresLoaded(true);
      }

      if (!contestRes.error) setContestRows((contestRes.data ?? []) as ContestRow[]);

      if (!inputRes.error) {
        const rows = (inputRes.data ?? []) as HoleInputRow[];
        // Same rule as scores: a hole whose blob is still in the air keeps the local copy.
        // A Wolf pick tapped on the tee must not be reverted by a refresh that raced it.
        const next: Record<number, HoleInputData> = {};
        for (const row of rows) {
          if (!opsRef.current.has(holeInputKey(row.hole_idx))) next[row.hole_idx] = row.data ?? {};
        }
        for (const [key, data] of Object.entries(holeInputsRef.current)) {
          if (opsRef.current.has(holeInputKey(Number(key)))) next[Number(key)] = data;
        }
        holeInputsRef.current = next;
        setHoleInputs(next);
      }

      if (!claimRes.error) {
        const rows = (claimRes.data ?? []) as ClaimRow[];
        setClaims((prev) => {
          const next: Record<string, { entrantId: string; measure: string }> = {};
          for (const row of rows) {
            next[row.contest_id] = { entrantId: row.entrant_id, measure: row.measure ?? '' };
          }
          // Same rule as scores — a claim still in the air outranks the server's copy.
          for (const [contestId, claim] of Object.entries(prev)) {
            if (opsRef.current.has(`contest:${contestId}`)) next[contestId] = claim;
          }
          return next;
        });
      }
    },
    []
  );

  const takeSeat = useCallback(
    async (joinCode: string, pickedTeam: JoinTeam, pickedEntrantId: string, tournamentId: string) => {
      setClaiming(true);
      setClaimError(null);
      try {
        // Re-claiming a seat this device already holds is a no-op server-side, which is what
        // makes a mid-round refresh cost nothing.
        const { error: claimErr } = await supabase.rpc('claim_entrant', {
          p_join_code: joinCode,
          p_entrant_id: pickedEntrantId,
        });
        if (claimErr) throw claimErr;

        const { data: rid, error: roundErr } = await supabase.rpc('open_round', {
          p_team_id: pickedTeam.id,
        });
        if (roundErr) throw roundErr;
        const openedRoundId = rid as string;

        writeSavedSeat(joinCode, { teamId: pickedTeam.id, entrantId: pickedEntrantId });
        setTeamId(pickedTeam.id);
        setEntrantId(pickedEntrantId);
        setRoundId(openedRoundId);
        // Open on the hole they teed off on — that is where they are standing.
        setHoleIdx(normalizeStartHole(pickedTeam.start_hole ?? undefined, holeCount));
        setJumpedToLive(false);
        setScoresLoaded(false);

        await loadRoundData(openedRoundId, tournamentId);
      } catch (err) {
        // The server's wording is the right wording — "Somebody already has that spot" is
        // clearer than anything this page would invent.
        setClaimError(errText(err));
        writeSavedSeat(joinCode, null);
      } finally {
        setClaiming(false);
      }
    },
    [loadRoundData, holeCount]
  );

  // Resume whatever seat this browser was on. A refresh in the middle of a round — which a
  // phone does on its own when it sleeps — must not cost anybody their place on the card.
  const resumedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!payload || !code || roundId) return;
    if (resumedRef.current === code) return;

    const saved = readSavedSeat(code);
    if (!saved) return;

    const savedTeam = payload.teams.find((t) => t.id === saved.teamId);
    const savedEntrant = savedTeam?.entrants.find((e) => e.id === saved.entrantId);
    if (!savedTeam || !savedEntrant) return;

    resumedRef.current = code;
    void takeSeat(code, savedTeam, savedEntrant.id, payload.tournament.id);
  }, [payload, code, roundId, takeSeat]);

  // Retry the initial score load in the background if it never landed. Scoring is allowed
  // to proceed meanwhile — a blank card that saves is better than a blocked one.
  useEffect(() => {
    if (!roundId || scoresLoaded || !tournament) return;
    const timer = window.setInterval(() => {
      void loadRoundData(roundId, tournament.id);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [roundId, scoresLoaded, tournament, loadRoundData]);

  // ── Writing ─────────────────────────────────────────────────────────────────

  /**
   * Put a number on the card, or take one off.
   *
   * In a scramble `targets` is the whole team, because that is where the team's one score
   * belongs. The UI change happens first and unconditionally; the network is somebody
   * else's problem, handled by the retry table.
   */
  const writeScore = useCallback(
    (targets: string[], hole: number, gross: number | null) => {
      if (!roundId || targets.length === 0) return;
      const rid = roundId;

      setScores((prev) => {
        const next = { ...prev };
        for (const id of targets) {
          if (gross == null) delete next[cellKey(id, hole)];
          else next[cellKey(id, hole)] = gross;
        }
        return next;
      });

      const cells = targets.map((id) => cellKey(id, hole));
      enqueue(`score:${hole}:${targets.join(',')}`, hole, cells, async () => {
        if (gross == null) {
          const { error } = await supabase
            .from('hole_scores')
            .delete()
            .eq('round_id', rid)
            .eq('hole_idx', hole)
            .in('entrant_id', targets);
          if (error) throw error;
          return;
        }
        const recordedAt = new Date().toISOString();
        const { error } = await supabase.from('hole_scores').upsert(
          targets.map((id) => ({
            round_id: rid,
            entrant_id: id,
            hole_idx: hole,
            gross,
            putts: null,
            recorded_at: recordedAt,
          })),
          { onConflict: 'round_id,entrant_id,hole_idx' }
        );
        if (error) throw error;
      });
    },
    [roundId, enqueue]
  );

  /**
   * Record something on a hole that is not a score — today, the Wolf's pick.
   *
   * Exactly the discipline `writeScore` follows, and for the same reason: this is tapped on
   * a tee box in a tree line, and a hole with no pick is a hole the Wolf engine skips and
   * nobody gets paid for. So the pick lands on screen first and unconditionally, and the
   * network is the retry table's problem. Keyed by HOLE, so five taps changing your mind
   * leave one pending write holding the last answer rather than five racing.
   *
   * THE MERGE HAPPENS IN POSTGRES, and only the changed keys go over the wire. `data` is the
   * whole of `HoleData` minus `scores` — one row shared by every game the group plays, so a
   * wolf pick, a banker's wagers and a set of drive zones all live in the same blob. A
   * client-side read-modify-write would send a blob that predates whatever a second phone
   * wrote in between, and silently drop it. Nobody would ever notice: the engine skips a
   * hole it has no pick for, and a hole that pays nobody reads as a rules quirk rather than
   * a lost write. `merge_hole_input` does `data || patch` inside the row lock, so there is no
   * window at all.
   *
   * Clearing a key is a JSON `null` in the patch, which REMOVES it. Not a stored null — the
   * engine must see no wolf, not a wolf that is null.
   *
   * Everything else is `writeScore`'s discipline, for the same reason: this is tapped on a
   * tee box in a tree line. The pick lands on screen first and unconditionally and the
   * network is the retry table's problem, keyed by HOLE so five taps changing your mind
   * leave one pending write holding the last answer rather than five racing.
   */
  const writeHoleInput = useCallback(
    (hole: number, patch: HoleInputData) => {
      if (!roundId) return;
      const rid = roundId;

      // The optimistic copy, applied from the REF rather than a setState updater — a
      // functional updater has not run by the time the write below is built.
      const blob: Record<string, unknown> = { ...(holeInputsRef.current[hole] ?? {}) };
      const wire: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(patch)) {
        if (value === undefined) {
          delete blob[key];
          wire[key] = null; // JSON null — the RPC reads it as "drop this key".
        } else {
          blob[key] = value;
          wire[key] = value;
        }
      }
      holeInputsRef.current = { ...holeInputsRef.current, [hole]: blob as HoleInputData };
      setHoleInputs(holeInputsRef.current);

      enqueue(holeInputKey(hole), hole, [], async () => {
        // The RPC returns the merged blob. It is deliberately not adopted here: a retry can
        // land long after the group has moved on, and `loadRoundData` is what reconciles
        // this phone with whatever the rest of the card wrote.
        const { error } = await supabase.rpc('merge_hole_input', {
          p_round_id: rid,
          p_hole_idx: hole,
          p_patch: wire,
        });
        if (error) throw error;
      });
    },
    [roundId, enqueue]
  );

  /**
   * The Wolf's pick for the hole on screen.
   *
   * `gameId` is taken and ignored: `HoleData['wolf']` is ONE record per hole, not one per
   * game, because that is the shape the engine reads and the phone app writes. Two Wolf
   * games on one card would therefore share a pick — which is harmless, since Wolf takes
   * exactly four players and this card has four, so any two of them are the same game.
   */
  const onWolfPick = useCallback(
    (_gameId: string, next: WolfPickValue | null) => {
      writeHoleInput(holeIdx, { wolf: next ?? undefined });
    },
    [holeIdx, writeHoleInput]
  );

  const onScore = useCallback(
    (targetEntrantId: string, next: number | null) => {
      const targets = scramble ? entrantIds : [targetEntrantId];
      writeScore(targets, holeIdx, next);
    },
    [scramble, entrantIds, holeIdx, writeScore]
  );

  const onClaim = useCallback(
    (contestId: string, claimEntrantId: string, measure: string) => {
      if (!roundId) return;
      const rid = roundId;
      setClaims((prev) => ({ ...prev, [contestId]: { entrantId: claimEntrantId, measure } }));

      enqueue(`contest:${contestId}`, holeIdx, [], async () => {
        const { error } = await supabase.from('contest_claims').upsert(
          { contest_id: contestId, round_id: rid, entrant_id: claimEntrantId, measure },
          { onConflict: 'contest_id,round_id' }
        );
        if (error) throw error;
      });
    },
    [roundId, holeIdx, enqueue]
  );

  const onClearClaim = useCallback(
    (contestId: string) => {
      if (!roundId) return;
      const rid = roundId;
      setClaims((prev) => {
        const next = { ...prev };
        delete next[contestId];
        return next;
      });

      enqueue(`contest:${contestId}`, holeIdx, [], async () => {
        const { error } = await supabase
          .from('contest_claims')
          .delete()
          .eq('contest_id', contestId)
          .eq('round_id', rid);
        if (error) throw error;
      });
    },
    [roundId, holeIdx, enqueue]
  );

  // ── Progress ────────────────────────────────────────────────────────────────
  //
  // Built into the shared Round shape so `holesPlayed` and `nextUnplayedHole` — the same
  // functions the phone app and the leaderboard use — answer it. "Thru 3" must mean the
  // same thing on every screen at the event, and on a shotgun it is emphatically not the
  // hole number they have reached.

  const roundModel = useMemo<Round>(() => {
    const holes: HoleData[] = Array.from({ length: holeCount }, (_, h) => {
      const hole = emptyHole(cardEntrantIds);
      for (const id of cardEntrantIds) {
        const value = scores[cellKey(id, h)];
        if (value != null) hole.scores[id] = { gross: value, putts: null };
      }
      return hole;
    });
    return {
      id: roundId ?? 'pending',
      // Only the score grid matters here — this shape exists to reach `holesPlayed` and
      // `nextUnplayedHole`, which read `holes` and nothing else.
      courseId: '',
      startedAt: 0,
      status: 'active',
      players: cardEntrantIds.map((playerId) => ({ playerId, teeId: '', courseHandicap: 0 })),
      holes,
      games: [],
      currentHole: holeIdx,
      startHole,
    };
  }, [holeCount, cardEntrantIds, scores, roundId, holeIdx, startHole]);

  const thru = useMemo(
    () => (cardEntrantIds.length > 0 ? holesPlayed(roundModel, cardEntrantIds) : 0),
    [roundModel, cardEntrantIds]
  );

  // On a resumed round, open on the hole they still owe rather than the one they teed off
  // on four hours ago. Once only — after that the arrows are theirs.
  useEffect(() => {
    if (!roundId || !scoresLoaded || jumpedToLive || holeCount === 0) return;
    setJumpedToLive(true);
    if (thru === 0) return;
    const next = nextUnplayedHole(roundModel, cardEntrantIds, startHole);
    if (next != null) setHoleIdx(next);
  }, [roundId, scoresLoaded, jumpedToLive, holeCount, thru, roundModel, cardEntrantIds, startHole]);

  const order = useMemo(() => playOrder(holeCount, startHole), [holeCount, startHole]);

  const contestsByHole = useMemo(() => {
    const map = new Map<number, ContestRow[]>();
    // Prefer the table rows, which carry ids. Fall back to the join payload so the banner
    // still appears even if the contests read failed — knowing it is there matters more
    // than being able to claim it.
    const source: ContestRow[] =
      contestRows.length > 0
        ? contestRows
        : (payload?.contests ?? []).map((c) => ({
            id: '',
            hole_idx: c.hole_idx,
            kind: c.kind,
            label: c.label,
          }));
    for (const row of source) {
      const list = map.get(row.hole_idx) ?? [];
      list.push(row);
      map.set(row.hole_idx, list);
    }
    return map;
  }, [contestRows, payload]);

  /** The next tee box with something on it, searching forward in THIS group's play order. */
  const upcoming = useMemo(() => {
    if (holeCount === 0) return null;
    for (let n = 1; n < holeCount; n++) {
      const idx = stepHole(holeIdx, n, holeCount);
      const list = contestsByHole.get(idx);
      if (list && list.length > 0) {
        return {
          holeNumber: idx + 1 + (tournament?.hole_number_offset ?? 0),
          labels: list.map((c) => contestText(c.kind, c.label)),
        };
      }
    }
    return null;
  }, [holeCount, holeIdx, contestsByHole, tournament]);

  const holeIsPending = useCallback(
    (hole: number) => Object.values(pendingHoles).some((h) => h === hole),
    [pendingHoles]
  );

  // Reads the state mirror rather than the ref, so the marker actually re-renders when a
  // write lands or starts failing.
  const cellIsPending = useCallback(
    (targetEntrantId: string, hole: number) => {
      const targets = scramble ? entrantIds : [targetEntrantId];
      return Object.prototype.hasOwnProperty.call(
        pendingHoles,
        `score:${hole}:${targets.join(',')}`
      );
    },
    [scramble, entrantIds, pendingHoles]
  );

  const steppers: Stepper[] = useMemo(() => {
    if (entrants.length === 0) return [];

    if (scramble) {
      // ONE stepper, whatever the team size. Four boxes would invite four different numbers
      // for the same ball, and the ball only went in once.
      //
      // Read the number from whichever seat has it rather than a fixed anchor: every member
      // carries the team's score, so any of them answers — and a seat added after a few
      // holes were scored would have no rows at all.
      const anchor = entrants[0];
      let value: number | null = null;
      for (const id of entrantIds) {
        const found = scores[cellKey(id, holeIdx)];
        if (found != null) {
          value = found;
          break;
        }
      }
      return [
        {
          entrantId: anchor.id,
          name: team?.name ?? 'Foursome',
          value,
          unsaved: cellIsPending(anchor.id, holeIdx),
        },
      ];
    }

    return cardEntrants.map((e) => ({
      entrantId: e.id,
      name: entrantName(e),
      value: scores[cellKey(e.id, holeIdx)] ?? null,
      unsaved: cellIsPending(e.id, holeIdx),
    }));
  }, [entrants, entrantIds, cardEntrants, scramble, team, scores, holeIdx, cellIsPending]);

  const holeContests: HoleCardContest[] = useMemo(
    () =>
      (contestsByHole.get(holeIdx) ?? []).map((row) => ({
        contestId: row.id || null,
        kind: row.kind,
        label: contestText(row.kind, row.label),
        claim: row.id ? claims[row.id] ?? null : null,
        unsaved: row.id ? Object.prototype.hasOwnProperty.call(pendingHoles, `contest:${row.id}`) : false,
      })),
    [contestsByHole, holeIdx, claims, pendingHoles]
  );

  // A contest is won by a PERSON, so only offer the people actually on the card.
  const contestEntrants: ContestEntrant[] = useMemo(
    () => cardEntrants.map((e) => ({ id: e.id, name: entrantName(e) })),
    [cardEntrants]
  );

  // ── Wolf ────────────────────────────────────────────────────────────────────
  //
  // WHOSE HOLE IT IS IS NOT A CHOICE THIS PAGE MAKES. The wolf rotates by hole index through
  // the game's own participant order — `participantIds[holeIdx % participantIds.length]` —
  // which is exactly what the phone app's WolfPickSheet does. The engine then trusts the
  // `wolfId` on the record it is handed and never re-derives it, so the rotation lives in
  // the UI and in nothing else. If this line and the phone's ever disagree, two devices on
  // the same round write two different wolves for the same hole and the money is wrong with
  // nothing anywhere reporting it.
  //
  // Note that it is the HOLE INDEX, not the group's play position: a shotgun group off hole
  // 5 opens with participant 1 (5 % 4), not participant 0. That is what the phone does and
  // what the engine assumes, so it is what happens here.
  //
  // PLACEHOLDER FOR PATRICK: on a shotgun that means the wolf order starts partway through
  // the group, and whoever begins depends on the tee they drew. Everyone still gets the same
  // number of turns over eighteen, so the money is even — but if you would rather a shotgun
  // group always start with the first name on the card, that is a change to the SHARED
  // rotation (phone, web and engine together), not something to fix here alone.

  /** Which games on this card are Wolf. Reads the config's own `type` rather than the engine
   *  registry, which would drag all nineteen engines into this page's bundle. */
  const wolfGames = useMemo(
    () => games.filter((g) => g.config?.type === 'wolf'),
    [games]
  );

  const wolfBlocks: WolfPickBlock[] = useMemo(() => {
    if (wolfGames.length === 0) return [];

    const names = new Map(cardEntrants.map((e) => [e.id, entrantName(e)]));
    const nameOf = (id: string) => names.get(id) ?? 'Player';

    // Any number on this hole means they have played it. A pick was due before that.
    const holeHasScore = cardEntrantIds.some((id) => scores[cellKey(id, holeIdx)] != null);
    const stored = holeInputs[holeIdx]?.wolf ?? null;
    const unsaved = Object.prototype.hasOwnProperty.call(pendingHoles, holeInputKey(holeIdx));

    const blocks: WolfPickBlock[] = [];
    for (const game of wolfGames) {
      const config = game.config as WolfConfig;
      const ids = config.participantIds ?? [];
      if (ids.length === 0) continue;

      const wolfId = ids[holeIdx % ids.length];
      // A stored pick naming somebody else belongs to a different rotation and is not this
      // block's — same guard the phone applies, and it keeps the hole reading as outstanding
      // rather than showing a pick that the engine will settle against another player.
      const pick = stored && stored.wolfId === wolfId ? stored : null;

      blocks.push({
        gameId: game.id,
        wolfId,
        wolfName: nameOf(wolfId),
        others: ids.filter((id) => id !== wolfId).map((id) => ({ id, name: nameOf(id) })),
        pick,
        loneMult: typeof config.loneMult === 'number' ? config.loneMult : 2,
        blindMult: typeof config.blindMult === 'number' ? config.blindMult : 3,
        outstanding: holeHasScore && pick === null,
        unsaved,
      });
    }
    return blocks;
  }, [wolfGames, cardEntrants, cardEntrantIds, scores, holeIdx, holeInputs, pendingHoles]);

  /** Holes that have been played but never got a pick. Marked on the strip so a hole missed
   *  three holes ago is findable, not just noticeable when you happen to walk back onto it. */
  const wolfOutstandingHoles = useMemo(() => {
    const out = new Set<number>();
    if (wolfGames.length === 0) return out;
    for (let h = 0; h < holeCount; h++) {
      const played = cardEntrantIds.some((id) => scores[cellKey(id, h)] != null);
      if (!played) continue;
      const stored = holeInputs[h]?.wolf ?? null;
      const expected = wolfGames.some((game) => {
        const ids = (game.config as WolfConfig).participantIds ?? [];
        return ids.length > 0 && stored?.wolfId === ids[h % ids.length];
      });
      if (!expected) out.add(h);
    }
    return out;
  }, [wolfGames, holeCount, cardEntrantIds, scores, holeInputs]);

  // ── Side games ──────────────────────────────────────────────────────────────
  //
  // A second, private thing happening inside this foursome. The event is individual stroke
  // play and none of that changes; this is the couple of dollars a skin the four of them
  // have going among themselves, settled within the group and nowhere else.
  //
  // NO MONEY IS COMPUTED ON THIS PAGE. The platform's rows are mapped into the domain
  // shapes the eighteen engines already read — see the header of `@shared/sync/toDomain`
  // for why there is one implementation of these rules and not two — and GroupMoney hands
  // them to the engines. Everything below is mapping.

  /** Course handicaps, for the net games. Not in the join payload, and RLS lets a player in
   *  the event read the entrants of it, so this is its own small read. Failure is silent:
   *  no handicaps simply means net is not offered. */
  useEffect(() => {
    if (!teamId) return;
    let live = true;
    void (async () => {
      const { data, error } = await supabase
        .from('entrants')
        .select('id,course_handicap')
        .eq('team_id', teamId);
      if (!live || error) return;
      const next: Record<string, number> = {};
      for (const row of (data ?? []) as Array<{ id: string; course_handicap: number | null }>) {
        if (typeof row.course_handicap === 'number') next[row.id] = row.course_handicap;
      }
      setHandicaps(next);
    })();
    return () => {
      live = false;
    };
  }, [teamId]);

  const gameCourse = useMemo(
    () =>
      courseFromPlatform({
        name: tournament?.course_name ?? 'Course',
        holePars: tournament?.hole_pars ?? [],
        holeStrokeIndexes: tournament?.hole_indexes ?? null,
      }),
    [tournament]
  );

  /**
   * Whether a NET side game can be settled honestly here.
   *
   * `allocateStrokes` reads stroke indexes as ranks, and a null in a Postgres `smallint[]`
   * arrives as 0 — which is below every remainder and silently awards an extra stroke on
   * every hole of every card. So a half-entered card is refused rather than repaired, and
   * a game with nobody carrying a handicap would net to exactly gross under a NET label.
   */
  const strokeIndexesUsable = useMemo(
    () => hasRealStrokeIndexes(tournament?.hole_indexes ?? null, holeCount),
    [tournament, holeCount]
  );

  const anyHandicap = useMemo(
    () => cardEntrantIds.some((id) => typeof handicaps[id] === 'number'),
    [cardEntrantIds, handicaps]
  );

  const netBlockedReason = !strokeIndexesUsable
    ? 'Net needs the course’s stroke indexes, and this event has not got a full set on file. Games settle gross.'
    : 'Nobody on this card has course strokes on file, so net would come out identical to gross. Games settle gross.';

  const gameRound = useMemo(() => {
    const rows: PlatformScore[] = [];
    for (const [key, gross] of Object.entries(scores)) {
      // `entrantId|holeIdx` — split at the LAST bar, since a uuid never contains one but
      // the delimiter is only guaranteed at the end.
      const at = key.lastIndexOf('|');
      if (at < 0) continue;
      rows.push({ entrantId: key.slice(0, at), holeIdx: Number(key.slice(at + 1)), gross });
    }
    return roundFromPlatform(
      {
        id: roundId ?? 'pending',
        startHole,
        entrants: cardEntrants.map((e) => ({
          id: e.id,
          displayName: entrantName(e),
          courseHandicap: handicaps[e.id] ?? null,
        })),
        scores: rows,
        games,
        // The non-score half of every hole. Without this the Wolf engine sees an empty
        // `hole.wolf` on all eighteen and skips the lot — a group would record every pick
        // and still be shown nothing.
        holeInputs: Object.entries(holeInputs).map(([hole, data]) => ({
          holeIdx: Number(hole),
          data,
        })),
      },
      holeCount
    );
  }, [scores, roundId, startHole, cardEntrants, handicaps, games, holeInputs, holeCount]);

  // ── Rendering ───────────────────────────────────────────────────────────────

  const submitCode = () => {
    const normalised = normalizeJoinCode(typed);
    if (!isValidJoinCode(normalised)) return;
    navigate(`/score/${normalised}`, { replace: true });
    setCode(normalised);
  };

  const leaveSeat = () => {
    if (pendingCount > 0 && !window.confirm(`${pendingCount} hole(s) have not reached the server yet. Leave anyway?`)) {
      return;
    }
    if (code) writeSavedSeat(code, null);
    resumedRef.current = null;
    opsRef.current.clear();
    syncPendingView();
    setRoundId(null);
    setTeamId(null);
    setEntrantId(null);
    setScores({});
    holeInputsRef.current = {};
    setHoleInputs({});
    setClaims({});
    setGames([]);
    setHandicaps({});
    setScoresLoaded(false);
    setPickTeamId(null);
  };

  if (session.error) {
    return (
      <div className="wrap sp">
        <style>{CSS}</style>
        <h1>Scoring</h1>
        <div className="card">
          <p className="err">{session.error}</p>
          <p className="sp-sub">
            The page needs a device identity before it can hold a spot on a card. No account,
            no password &mdash; but it does need this one call to work.
          </p>
          <button type="button" onClick={session.retry}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!session.ready) {
    return (
      <div className="wrap sp">
        <style>{CSS}</style>
        <h1>Scoring</h1>
        <p className="muted">Getting you on…</p>
      </div>
    );
  }

  // 1. No code yet.
  if (!code) {
    const problem = joinCodeProblem(typed);
    return (
      <div className="wrap sp">
        <style>{CSS}</style>
        <h1>Score your round</h1>
        <p className="sp-lede muted">
          Type the code from your cart sign. No app, no account.
        </p>
        <div className="card">
          <label htmlFor="sp-code">Event code</label>
          <input
            id="sp-code"
            className="sp-code-input tnum"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submitCode();
            }}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            inputMode="text"
            placeholder="ABC123"
            maxLength={12}
          />
          {problem ? <p className="err">{problem}</p> : null}
          <button
            type="button"
            className="sp-big"
            disabled={!isValidJoinCode(typed)}
            onClick={submitCode}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // 2. Fetching the event.
  if (joining && !payload) {
    return (
      <div className="wrap sp">
        <style>{CSS}</style>
        <h1>{formatJoinCode(code)}</h1>
        <p className="muted">Looking up the event…</p>
      </div>
    );
  }

  if (joinError && !payload) {
    return (
      <div className="wrap sp">
        <style>{CSS}</style>
        <h1>{formatJoinCode(code)}</h1>
        <div className="card">
          <p className="err">{joinError}</p>
          <div className="sp-actions">
            <button type="button" onClick={() => void runJoin(code)}>
              Try again
            </button>
            <button
              type="button"
              className="secondary"
              onClick={() => {
                setCode('');
                setTyped('');
                navigate('/score', { replace: true });
              }}
            >
              Different code
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!payload || !tournament) {
    return (
      <>
        <style>{CSS}</style>
        <LoadingScore />
      </>
    );
  }

  // 3. Picking a team, then a seat.
  if (!roundId || !teamId || !entrantId) {
    const chosen = payload.teams.find((t) => t.id === pickTeamId) ?? null;

    return (
      <div className="wrap sp">
        <style>{CSS}</style>
        <h1>{tournament.name}</h1>
        <p className="sp-lede muted">
          {tournament.course_name}
          {tournament.status === 'draft' ? ' · not started yet' : null}
          {tournament.status === 'final' ? ' · finished' : null}
        </p>

        {claimError ? (
          <div className="card sp-alert">
            <p className="err">{claimError}</p>
            <p className="sp-sub">
              If that spot is yours and this phone has forgotten it &mdash; a new browser, or
              cleared data &mdash; the scorer&rsquo;s table has to release it for you.
            </p>
          </div>
        ) : null}

        {!chosen ? (
          <>
            <h2 className="sp-h2">Which foursome are you with?</h2>
            {payload.teams.length === 0 ? (
              <div className="card">
                <p>Nobody has signed up for this event yet.</p>
              </div>
            ) : null}
            <div className="sp-list">
              {payload.teams.map((t) => {
                const taken = t.entrants.filter((e) => e.claimed).length;
                return (
                  <button
                    key={t.id}
                    type="button"
                    className="sp-pick"
                    onClick={() => {
                      setClaimError(null);
                      setPickTeamId(t.id);
                    }}
                  >
                    <span className="sp-pick-main">{t.name}</span>
                    <span className="sp-pick-sub">
                      {t.slot_label
                        ? `Starts on ${t.slot_label}`
                        : t.start_hole != null
                          ? `Starts on hole ${t.start_hole + 1 + tournament.hole_number_offset}`
                          : 'Starts on hole 1'}
                      {' · '}
                      {taken} of {t.entrants.length} spots taken
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <h2 className="sp-h2">Which one are you?</h2>
            <p className="sp-lede muted">{chosen.name}</p>
            <div className="sp-list">
              {chosen.entrants.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  className="sp-pick"
                  disabled={e.claimed || claiming}
                  onClick={() => void takeSeat(code, chosen, e.id, tournament.id)}
                >
                  <span className="sp-pick-main">{entrantName(e)}</span>
                  <span className="sp-pick-sub">{e.claimed ? 'Taken' : 'Free'}</span>
                </button>
              ))}
            </div>
            <div className="sp-actions">
              <button type="button" className="secondary" onClick={() => setPickTeamId(null)}>
                Back to foursomes
              </button>
            </div>
            <p className="sp-sub">
              Any one of you can keep the card &mdash; take your own spot and start scoring.
            </p>
          </>
        )}
      </div>
    );
  }

  // 4. Scoring.
  const holeNumber = holeIdx + 1 + tournament.hole_number_offset;
  const par = tournament.hole_pars[holeIdx] ?? null;

  return (
    <div className="wrap sp sp-scoring">
      <style>{CSS}</style>

      <header className="sp-bar">
        <div className="sp-bar-left">
          <div className="sp-bar-team">{team?.name ?? 'Your foursome'}</div>
          <div className="sp-bar-event">{tournament.name}</div>
        </div>
        <div className="sp-bar-right">
          <div className="sp-thru tnum">thru {thru}</div>
          <div className={`sp-sync ${pendingCount > 0 ? 'sp-sync-busy' : ''}`}>
            {pendingCount > 0
              ? `${pendingCount} hole${pendingCount === 1 ? '' : 's'} waiting to sync`
              : online
                ? 'All saved'
                : 'Offline'}
          </div>
        </div>
      </header>

      {!scoresLoaded ? (
        <div className="sp-warn">
          Could not read what is already on this card. Keep scoring &mdash; everything you
          enter still saves, and the rest will appear when signal comes back.
        </div>
      ) : null}

      <div className="sp-nav">
        <button
          type="button"
          className="sp-arrow"
          aria-label="Previous hole"
          onClick={() => setHoleIdx((h) => stepHole(h, -1, holeCount))}
        >
          &lsaquo;
        </button>
        <div className="sp-nav-mid">
          <span className="sp-nav-pos tnum">
            Hole {playPosition(holeIdx, holeCount, startHole)} of {holeCount} for your foursome
          </span>
        </div>
        <button
          type="button"
          className="sp-arrow"
          aria-label="Next hole"
          onClick={() => setHoleIdx((h) => stepHole(h, +1, holeCount))}
        >
          &rsaquo;
        </button>
      </div>

      <HoleCard
        holeNumber={holeNumber}
        par={par}
        playPosition={playPosition(holeIdx, holeCount, startHole)}
        holeCount={holeCount}
        scramble={scramble}
        steppers={steppers}
        onScore={onScore}
        contests={holeContests}
        contestEntrants={contestEntrants}
        onClaim={onClaim}
        onClearClaim={onClearClaim}
        wolfBlocks={wolfBlocks}
        onWolfPick={onWolfPick}
        upcoming={upcoming}
      />

      <div className="sp-strip" role="group" aria-label="Jump to a hole">
        {order.map((idx) => {
          const done =
            cardEntrantIds.length > 0 &&
            cardEntrantIds.every((id) => scores[cellKey(id, idx)] != null);
          const hasContest = (contestsByHole.get(idx)?.length ?? 0) > 0;
          // A played hole with no Wolf pick is money the group is not going to be paid.
          // Marked here so it can be found later, not only stumbled back onto.
          const wolfMissing = wolfOutstandingHoles.has(idx);
          return (
            <button
              key={idx}
              type="button"
              className={[
                'sp-chip',
                idx === holeIdx ? 'sp-chip-now' : '',
                done ? 'sp-chip-done' : '',
                holeIsPending(idx) ? 'sp-chip-pending' : '',
                wolfMissing ? 'sp-chip-wolf' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setHoleIdx(idx)}
              aria-label={
                wolfMissing
                  ? `Hole ${idx + 1 + tournament.hole_number_offset} — no Wolf pick recorded`
                  : undefined
              }
            >
              <span className="tnum">{idx + 1 + tournament.hole_number_offset}</span>
              {hasContest ? <span className="sp-chip-star">&bull;</span> : null}
              {wolfMissing ? <span className="sp-chip-wolf-mark">!</span> : null}
            </button>
          );
        })}
      </div>

      {/* The group's own side games. Below the card and the hole strip on purpose: scoring
          is what this page is for, and the money is what it is worth afterwards. */}
      <Suspense fallback={null}>
        <GroupMoney
          course={gameCourse}
          round={gameRound}
          players={contestEntrants}
          strokeIndexesUsable={strokeIndexesUsable}
        />

        <GameSetup
          roundId={roundId}
          course={gameCourse}
          round={gameRound}
          players={contestEntrants}
          games={games}
          onGamesChange={setGames}
          allowNet={strokeIndexesUsable && anyHandicap}
          netBlockedReason={netBlockedReason}
        />
      </Suspense>

      <div className="sp-foot">
        <div className="sp-foot-buttons">
          {/* Until there is a realtime feed, this is how a second phone in the group picks
              up what the first one entered. The merge keeps anything still unsynced. */}
          <button
            type="button"
            className="secondary"
            onClick={() => void loadRoundData(roundId, tournament.id)}
          >
            Refresh card
          </button>
          <button type="button" className="secondary" onClick={leaveSeat}>
            Not my card
          </button>
        </div>
        <span className="sp-foot-note">
          {formatJoinCode(code)} · {scramble ? 'Scramble' : 'Individual'} ·{' '}
          {tournament.scoring === 'net' ? 'Net' : 'Gross'}
        </span>
      </div>
    </div>
  );
}

const CSS = `
.sp { max-width: 560px; padding-bottom: 40px; }
.sp-lede { margin-top: -4px; }
.sp-sub { font-size: 13px; color: var(--ink-dim); line-height: 1.45; }
.sp .card .sp-sub { color: var(--ink-dim); }
.sp-h2 { margin-top: 22px; font-size: 18px; }
.sp-big { width: 100%; min-height: 60px; font-size: 18px; margin-top: 12px; }
.sp-alert { border-color: var(--danger); margin-bottom: 12px; }
.sp-actions { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 14px; }
.sp-actions button { flex: 1 1 auto; min-height: 52px; }

.sp-code-input {
  font-size: 30px; font-weight: 800; letter-spacing: 6px; text-align: center;
  text-transform: uppercase; min-height: 68px;
}

.sp-list { display: flex; flex-direction: column; gap: 10px; margin-top: 12px; }
.sp-pick {
  display: flex; flex-direction: column; align-items: flex-start; gap: 3px;
  width: 100%; min-height: 68px; text-align: left; padding: 12px 14px;
  background: var(--paper); color: var(--ink); border: 1px solid var(--paper-border);
}
.sp-pick:disabled { opacity: .45; }
.sp-pick-main { font-size: 17px; font-weight: 800; }
.sp-pick-sub { font-size: 13px; font-weight: 600; color: var(--ink-dim); }

/* Scoring */
.sp-scoring { padding-top: 12px; }
.sp-bar {
  position: sticky; top: 0; z-index: 5;
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 10px 0 12px; margin-bottom: 4px;
  background: var(--bg); border-bottom: 1px solid rgba(243,239,226,.12);
}
.sp-bar-team { font-size: 16px; font-weight: 800; }
.sp-bar-event { font-size: 12px; color: var(--text-dim); }
.sp-bar-right { text-align: right; }
.sp-thru { font-size: 20px; font-weight: 800; color: var(--brass); line-height: 1.1; }
.sp-sync { font-size: 11px; font-weight: 700; color: var(--text-dim); }
.sp-sync-busy { color: var(--brass); }

.sp-warn {
  background: rgba(208,67,59,.14); border: 1px solid var(--danger); border-radius: 8px;
  padding: 10px 12px; font-size: 13px; line-height: 1.4; margin-bottom: 12px;
}

.sp-nav { display: flex; align-items: center; gap: 10px; margin: 8px 0 10px; }
.sp-nav-mid { flex: 1 1 auto; text-align: center; }
.sp-nav-pos { font-size: 13px; font-weight: 700; color: var(--text-dim); }
.sp-arrow {
  min-width: 72px; min-height: 60px; font-size: 32px; line-height: 1; font-weight: 800;
  background: transparent; color: var(--text); border: 1px solid var(--text-dim);
  touch-action: manipulation; -webkit-tap-highlight-color: transparent;
}
.sp-arrow:active { background: rgba(243,239,226,.08); }

.sp-strip {
  display: grid; grid-template-columns: repeat(9, 1fr); gap: 6px; margin-top: 14px;
}
.sp-chip {
  position: relative; min-height: 44px; padding: 0; font-size: 14px; font-weight: 700;
  background: transparent; color: var(--text-dim); border: 1px solid rgba(243,239,226,.18);
}
.sp-chip-done { background: rgba(87,168,108,.22); color: var(--text); border-color: var(--ok); }
.sp-chip-pending { border-color: var(--brass); }
.sp-chip-now { outline: 2px solid var(--brass); outline-offset: -2px; color: var(--text); }
.sp-chip-star {
  position: absolute; top: 2px; right: 4px; font-size: 14px; line-height: 1; color: var(--brass);
}
/* Played, but the Wolf pick never went in — that hole pays nobody until it does. */
.sp-chip-wolf { border-color: var(--danger); color: var(--text); }
.sp-chip-wolf-mark {
  position: absolute; bottom: 1px; right: 5px;
  font-size: 13px; font-weight: 800; line-height: 1; color: var(--danger);
}

.sp-foot {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  margin-top: 20px; flex-wrap: wrap;
}
.sp-foot-buttons { display: flex; gap: 10px; flex-wrap: wrap; }
.sp-foot button { min-height: 48px; }
.sp-foot-note { font-size: 12px; color: var(--text-dim); }
`;
